/**
 * Phone notifications (Web Push) from the browser's side.
 *
 * The platform rules, which no app can change: the browser asks permission itself, only
 * in response to a tap, and an iPhone only offers push to a web app added to the Home
 * Screen (iOS 16.4+). Everything here works inside those rules.
 */

import { api } from "./api";

export interface PushConfig {
  available: boolean;
  public_key: string | null;
  devices: number;
}

export type PushState =
  | "unsupported" // this browser cannot do it at all
  | "install-first" // an iPhone, not yet added to the Home Screen
  | "denied" // the person said no; only the browser's own settings can undo it
  | "off" // could be turned on with a tap
  | "on";

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function isIos(): boolean {
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
}

export function isInstalled(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.ready;
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  return (await registration()).pushManager.getSubscription();
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return isIos() && !isInstalled() ? "install-first" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission === "granted" && (await currentSubscription())) return "on";
  return "off";
}

function keyBytes(base64url: string): Uint8Array {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(
    Math.ceil(base64url.length / 4) * 4,
    "=",
  );
  const raw = atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

function sameKey(subscription: PushSubscription, key: Uint8Array): boolean {
  const current = subscription.options?.applicationServerKey;
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((value, index) => value === key[index]);
}

async function save(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  await api.post("/push/subscriptions", { endpoint: json.endpoint, keys: json.keys });
}

/** The person (or the browser, for them) said no: only the browser's settings undo it. */
export class PushDenied extends Error {}
/** The prompt was closed without an answer, or the browser chose not to show it. */
export class PushDismissed extends Error {}

/**
 * Where this browser keeps the switch, once notifications are blocked. No web page can
 * ask a second time — `requestPermission` answers "denied" without showing anything —
 * so the most an app can do is point at the right setting.
 */
export function howToAllow(): string {
  const agent = navigator.userAgent;
  // Android first: `isIos` also matches touch-screen Macs, which is all an emulator is.
  if (/Android/i.test(agent)) {
    return isInstalled()
      ? "Press and hold The Sheet's icon → App info → Notifications, and allow them."
      : "Tap the icon to the left of the address → Permissions → Notifications, and allow them.";
  }
  if (isIos()) {
    return isInstalled()
      ? "Open the iPhone Settings app → Notifications → The Sheet, and turn on Allow Notifications."
      : "Add The Sheet to your Home Screen (Share → Add to Home Screen) and open it from there.";
  }
  if (/Firefox\//.test(agent)) {
    return "Click the icon to the left of the address, remove the block on notifications, then reload the page.";
  }
  if (/Safari\//.test(agent) && !/Chrome|Chromium|Edg\//.test(agent)) {
    return "In Safari → Settings → Websites → Notifications, set this site to Allow, then reload the page.";
  }
  return "Click the icon to the left of the address, set Notifications to Allow, then reload the page.";
}

/** Ask, subscribe, and tell the server. Must be called from a tap. */
export async function turnOn(): Promise<void> {
  const config = await api.get<PushConfig>("/push/config");
  if (!config.available || !config.public_key) throw new Error("Notifications are off on this install.");
  const permission = await Notification.requestPermission();
  if (permission === "denied") throw new PushDenied("Notifications are blocked.");
  if (permission !== "granted") throw new PushDismissed("The prompt was closed.");
  const key = keyBytes(config.public_key);
  const manager = (await registration()).pushManager;
  let subscription = await manager.getSubscription();
  // A subscription made with an old server key can no longer be sent to.
  if (subscription && !sameKey(subscription, key)) {
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await manager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key as BufferSource,
  });
  await save(subscription);
}

export async function turnOff(): Promise<void> {
  const subscription = await currentSubscription();
  if (!subscription) return;
  await api.delete("/push/subscriptions", { body: { endpoint: subscription.endpoint } });
  await subscription.unsubscribe();
}

/**
 * Re-send this device's subscription when the app opens. Push services rotate them now
 * and then; the server treats a repeat as a no-op. Silent: never prompts anybody.
 */
export async function resync(): Promise<void> {
  if (!pushSupported() || Notification.permission !== "granted") return;
  const subscription = await currentSubscription();
  if (subscription) await save(subscription);
}
